import { logError } from "@/shared/utils/datadog-logger";
import { formatErrorMessage } from "@/shared/utils/error-formatting";
import { Component, ErrorInfo, ReactNode } from "react";

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * Error Boundary component that catches errors in its children components
 * and displays a fallback UI when an error occurs.
 *
 * Features:
 * - Automatic Datadog error logging
 * - Reset functionality for error recovery
 * - Custom fallback UI support
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={(error, reset) => <ErrorDisplay error={error} onRetry={reset} />}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        logError("ErrorBoundary caught an error", error, {
            componentStack: errorInfo.componentStack,
        });
    }

    reset = (): void => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback function
            if (typeof this.props.fallback === "function") {
                return this.props.fallback(this.state.error as Error, this.reset);
            }

            // Custom fallback ReactNode
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            const errorMessage = this.state.error ? formatErrorMessage(this.state.error) : "Een onverwachte fout is opgetreden";

            return (
                <div className="border-destructive/30 bg-destructive/10 text-destructive rounded-md border p-6">
                    <h2 className="text-l mb-2 font-semibold">Fout</h2>
                    <p className="mb-4">{errorMessage}</p>
                    <button
                        onClick={this.reset}
                        className="bg-destructive/20 hover:bg-destructive/30 text-destructive rounded-md px-4 py-2 transition-colors"
                    >
                        Opnieuw proberen
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}
